/**
 * Open an infographic in a new browser tab as a standalone, high-resolution view
 * or full-screen live slide presentation for sharing during calls (Meet / Zoom / Teams).
 */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function openInfographicInNewTab(title: string, src: string): void {
  const safeTitle = esc(title || "SellSynq Live Slide");
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${safeTitle} · SellSynq Live Slide</title>
<style>
  :root { color-scheme: light dark; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: #0b0f19;
    color: #f1f5f9;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
  }
  header {
    padding: 12px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: #111827;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 10;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
  body.fullscreen-present header {
    display: none;
  }
  body.fullscreen-present main {
    padding: 0;
    height: 100vh;
  }
  body.fullscreen-present .img-card {
    border: none;
    background: transparent;
    box-shadow: none;
    padding: 0;
    max-width: 100vw;
    max-height: 100vh;
  }
  body.fullscreen-present .img-card img {
    max-height: 100vh;
    max-width: 100vw;
    border-radius: 0;
  }
  .title-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .logo-badge {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: white;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 6px;
    letter-spacing: 0.04em;
  }
  h1 {
    margin: 0;
    font-size: 14.5px;
    font-weight: 600;
    color: #f8fafc;
    max-width: 500px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .btn {
    background: #1e293b;
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #f8fafc;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .btn:hover {
    background: #334155;
    border-color: rgba(255, 255, 255, 0.25);
  }
  .btn-primary {
    background: #4f46e5;
    border-color: #6366f1;
    color: #fff;
  }
  .btn-primary:hover {
    background: #4338ca;
  }
  .present-pill {
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid rgba(16, 185, 129, 0.4);
    color: #34d399;
  }
  .present-pill:hover {
    background: rgba(16, 185, 129, 0.25);
  }
  main {
    flex: 1;
    padding: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: radial-gradient(circle at center, #1e293b 0%, #0b0f19 100%);
    position: relative;
  }
  .img-card {
    background: #0f172a;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 95vw;
  }
  .img-card img {
    max-width: 100%;
    max-height: calc(100vh - 120px);
    border-radius: 8px;
    object-fit: contain;
    display: block;
  }
  .floating-hint {
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(15, 23, 42, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.12);
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 11.5px;
    color: #94a3b8;
    pointer-events: none;
    opacity: 0.8;
  }
  @media print {
    header, .floating-hint { display: none; }
    body, main { background: #fff !important; }
    .img-card { border: none !important; box-shadow: none !important; padding: 0 !important; }
    .img-card img { max-height: 100% !important; }
  }
</style>
</head>
<body>
<header id="top-header">
  <div class="title-group">
    <span class="logo-badge">SELLSYNQ LIVE</span>
    <h1>${safeTitle}</h1>
  </div>
  <div class="actions">
    <button onclick="toggleFullscreen()" class="btn present-pill" title="Present full screen to share on Google Meet / Zoom">
      🖥️ Present Fullscreen (Share Screen)
    </button>
    <button onclick="copyImage()" id="copy-btn" class="btn" title="Copy slide image to clipboard">
      📋 Copy Slide
    </button>
    <a href="${src}" download="sellsynq-slide.png" class="btn">⬇ Download</a>
    <button onclick="window.print()" class="btn btn-primary">🖨 Print / PDF</button>
  </div>
</header>
<main onclick="handleClick()">
  <div class="img-card">
    <img id="slide-img" src="${src}" alt="${safeTitle}" />
  </div>
</main>
<div class="floating-hint" id="status-hint">Tip: Click "Present Fullscreen" to share this clean slide directly with your client. Press Esc to exit.</div>

<script>
  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        document.body.classList.add('fullscreen-present');
      }).catch(() => {});
    } else {
      document.exitFullscreen().then(() => {
        document.body.classList.remove('fullscreen-present');
      }).catch(() => {});
    }
  }

  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) {
      document.body.classList.remove('fullscreen-present');
    } else {
      document.body.classList.add('fullscreen-present');
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'f' || e.key === 'F') {
      toggleFullscreen();
    }
  });

  async function copyImage() {
    const btn = document.getElementById('copy-btn');
    try {
      const img = document.getElementById('slide-img');
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        btn.textContent = '✓ Copied!';
        setTimeout(() => { btn.textContent = '📋 Copy Slide'; }, 2500);
      }, 'image/png');
    } catch (err) {
      btn.textContent = '✓ Downloaded';
      const a = document.createElement('a');
      a.href = "${src}";
      a.download = "sellsynq-slide.png";
      a.click();
      setTimeout(() => { btn.textContent = '📋 Copy Slide'; }, 2500);
    }
  }
</script>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
